import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const RegisterPage = () => {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Preencha todos os campos");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Você precisa aceitar os Termos de Uso para continuar");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Conta criada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-[#F5F2EB] flex overflow-hidden">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1529081606-ca4a5a53beab?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Architectural details"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F4C5C]/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-serif text-white text-3xl font-medium mb-3">Comece sua jornada técnica</h2>
          <p className="text-white/90 text-base">Crie sua conta e organize seu raciocínio profissional</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-in py-6">
          <div className="text-center mb-6">
            <h1 className="font-serif text-3xl font-medium text-[#1A202C] mb-1">PERYTUS</h1>
            <p className="text-[#718096] text-sm">Criar nova conta</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#4A5568]">Nome completo</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#4A5568]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#4A5568]">Senha</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#718096]">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#4A5568]">Confirmar senha</Label>
              <Input id="confirmPassword" type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repita a senha" />
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F5F2EB] rounded-lg border border-[#E2E8F0]">
              <Checkbox id="terms" checked={acceptedTerms} onCheckedChange={(checked) => setAcceptedTerms(checked)} />
              <div className="text-sm">
                <label htmlFor="terms" className="text-[#4A5568] cursor-pointer text-xs">
                  Declaro que li e concordo com os <Link to="/termos-de-uso" target="_blank" className="text-[#0F4C5C] hover:underline font-medium">Termos de Uso</Link> e <Link to="/licenca-de-uso" target="_blank" className="text-[#0F4C5C] hover:underline font-medium">Licença do PERYTUS</Link>
                </label>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full py-5">
              {loading ? <span>Criando...</span> : <span className="flex items-center gap-2"><UserPlus size={18} /> Criar conta</span>}
            </Button>
          </form>
          <p className="text-center mt-6 text-[#718096] text-sm">
            Já tem uma conta? <Link to="/login" className="text-[#0F4C5C] hover:underline font-medium">Entrar</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
